import React, { useEffect, useState } from "react";
import { Box, Stack, Dialog, TextField, CircularProgress } from "@mui/material";
import ErrorMsg from "../ErrorMsg";
import BtnPrimary from "../PrimaryButton";
import BtnSecondary from "../SecondaryButton";
import { tokens } from "../../theme";

/**
 * One correction, one reason, many employees, one day.
 *
 * The case this serves is a reader failing: a crew worked, none of them
 * registered, and one supervisor knows it. Every employee still gets their own
 * regularization record — a single shared one would make the audit trail worse,
 * since a correction has to be traceable per person. What is shared is the
 * reason and the times, which is exactly what the supervisor types once.
 *
 * The remark is required here for the same reason it is on a single correction,
 * and more so: forty asserted days with no explanation is forty unexplained
 * changes to somebody's pay.
 */

const toTimeInput = (mins) =>
  mins == null
    ? ""
    : `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

const fromTimeInput = (value) => {
  if (!value) return null;
  const [h, m] = value.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const fieldSx = {
  "& .MuiInputBase-root": { fontSize: "0.875rem", borderRadius: "8px", background: tokens.card },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.border },
};

const Label = ({ children }) => (
  <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.25rem" }}>
    {children}
  </Box>
);

const BulkAttestDialog = ({
  open,
  dateLabel,
  employees = [],   // [{ id, name, statusLabel }]
  shiftStart = null,
  shiftEnd = null,
  onClose,
  onSubmit,
}) => {
  const [inTime, setInTime] = useState("");
  const [outTime, setOutTime] = useState("");
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Field-level, raised on leaving the control. `error` keeps only what is not
  // about a single field: a failed write.
  const [fieldErrors, setFieldErrors] = useState({});
  const markField = (field, message) =>
    setFieldErrors((f) => ({ ...f, [field]: message || "" }));
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Seed from the shift's own hours where they are known. A crew whose reader
    // failed worked their rostered hours until someone says otherwise, and
    // making the supervisor retype 09:00–18:00 forty times invites typos.
    setInTime(toTimeInput(shiftStart));
    setOutTime(toTimeInput(shiftEnd));
    setRemark("");
    setError("");
    setSaving(false);
    setResults(null);
  }, [open, shiftStart, shiftEnd]);

  const nextIn = fromTimeInput(inTime);
  const nextOut = fromTimeInput(outTime);

  // Defined once so the blur and the button cannot disagree.
  const checkTimes = () => {
    if (nextIn == null && nextOut == null) return "Set a check-in or check-out time.";
    if (nextIn != null && nextOut != null && nextOut <= nextIn) return "Check-out must be after check-in.";
    return "";
  };
  const checkRemark = () =>
    !remark.trim() ? "A remark is required — it's the record of why these changed." : "";

  const save = async () => {
    setError("");
    const times = checkTimes();
    const needsRemark = checkRemark();
    if (times || needsRemark) {
      markField("times", times);
      markField("remark", needsRemark);
      return;
    }

    setSaving(true);
    const payload = { reason: remark.trim(), employeeIds: employees.map((e) => e.id) };
    if (nextIn != null) payload.updatedClockInTime = nextIn;
    if (nextOut != null) payload.updatedClockOutTime = nextOut;

    const res = await onSubmit(payload);
    setSaving(false);

    if (!res?.success) {
      setError(res?.message || "Couldn't apply the corrections.");
      return;
    }
    // Never close straight into a success toast: a bulk write can partly fail,
    // and the supervisor is entitled to see exactly who did not take.
    const failed = (res.data?.results || []).filter((r) => !r.ok);
    if (failed.length) setResults(res.data.results);
    else onClose(true);
  };

  const failed = (results || []).filter((r) => !r.ok);
  const applied = (results || []).filter((r) => r.ok);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : () => onClose(Boolean(results))}
      slotProps={{
        paper: { sx: { width: "26rem", maxWidth: "94vw", borderRadius: "12px", p: "1.25rem" } },
      }}
    >
      {results ? (
        <>
          <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text, mb: "0.25rem" }}>
            {applied.length} corrected, {failed.length} could not be
          </Box>
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1rem" }}>
            The corrections that succeeded have been applied and are not affected by
            the ones that didn't.
          </Box>
          <Box sx={{ maxHeight: "14rem", overflowY: "auto", mb: "1rem" }}>
            {failed.map((r) => (
              <Stack key={r.employeeId} direction="row" spacing={1} sx={{ mb: "0.5rem" }}>
                <Box sx={{ color: tokens.danger, fontSize: "0.8125rem", fontWeight: 600 }}>✗</Box>
                <Box>
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.text }}>{r.name}</Box>
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{r.message}</Box>
                </Box>
              </Stack>
            ))}
          </Box>
          <Stack direction="row" justifyContent="flex-end">
            <BtnPrimary onClick={() => onClose(true)} sx={{ borderRadius: "8px" }}>
              Done
            </BtnPrimary>
          </Stack>
        </>
      ) : (
        <>
          <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>
            Correct {employees.length} {employees.length === 1 ? "employee" : "employees"}
          </Box>
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1rem" }}>{dateLabel}</Box>

          <Stack direction="row" spacing={1.25} sx={{ mb: "1rem" }}>
            <Box sx={{ flex: 1 }}>
              <Label>Check in</Label>
              <TextField type="time" size="small" fullWidth value={inTime}
                error={Boolean(fieldErrors.times)}
                onChange={(e) => { setInTime(e.target.value); markField("times", ""); }}
                onBlur={() => markField("times", checkTimes())} sx={fieldSx} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Label>Check out</Label>
              <TextField type="time" size="small" fullWidth value={outTime}
                error={Boolean(fieldErrors.times)}
                onChange={(e) => { setOutTime(e.target.value); markField("times", ""); }}
                onBlur={() => markField("times", checkTimes())} sx={fieldSx} />
            </Box>
          </Stack>

          <ErrorMsg>{fieldErrors.times}</ErrorMsg>

          <Box sx={{ mb: "1rem" }}>
            <Label>Remark (required)</Label>
            <TextField size="small" fullWidth multiline minRows={2}
              placeholder="e.g. reader offline on the floor"
              value={remark}
              error={Boolean(fieldErrors.remark)}
              onChange={(e) => { setRemark(e.target.value); markField("remark", ""); }}
              onBlur={() => markField("remark", checkRemark())} sx={fieldSx} />
            <ErrorMsg>{fieldErrors.remark}</ErrorMsg>
          </Box>

          <Box
            sx={{
              maxHeight: "9rem",
              overflowY: "auto",
              border: `1px solid ${tokens.border}`,
              borderRadius: "8px",
              p: "0.5rem 0.75rem",
              mb: "1rem",
            }}
          >
            {employees.map((e) => (
              <Stack key={e.id} direction="row" justifyContent="space-between" sx={{ py: "0.25rem" }}>
                <Box sx={{ fontSize: "0.8125rem", color: tokens.text }}>{e.name}</Box>
                <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{e.statusLabel}</Box>
              </Stack>
            ))}
          </Box>

          {error && <Box sx={{ fontSize: "0.8125rem", color: tokens.danger, mb: "0.75rem" }}>{error}</Box>}

          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1rem", lineHeight: 1.5 }}>
            Each employee gets their own correction record in your name, with this
            remark, applied immediately.
          </Box>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <BtnSecondary onClick={() => onClose(false)} disabled={saving}
              sx={{ height: "2.25rem", px: "1rem", borderRadius: "8px" }}>
              Cancel
            </BtnSecondary>
            <BtnPrimary onClick={save} disabled={saving || employees.length === 0}
              sx={{ height: "2.25rem", px: "1.25rem", borderRadius: "8px" }}>
              {saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Apply corrections"}
            </BtnPrimary>
          </Stack>
        </>
      )}
    </Dialog>
  );
};

export default BulkAttestDialog;
